/** 운영 대시보드 — 학생/수업/오늘 출석 요약 + 빠른 이동 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HiOutlineUsers, HiOutlineBookOpen } from 'react-icons/hi2';
import { IoCheckmarkCircle, IoCalendarOutline } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useConsoleAcademy } from '../../components/console/ConsoleLayout';
import { StatGrid, StatCard, StatIcon, StatBody, Card, Empty } from './consoleUi';

const fmtDate = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const KO_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEK_ORDER = ['월', '화', '수', '목', '금', '토', '일'];

const ConsoleDashboard = () => {
  const { academy } = useConsoleAcademy();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ students: 0, classes: 0, present: 0, attTotal: 0 });
  const [todayClasses, setTodayClasses] = useState([]);
  const [weekDist, setWeekDist] = useState([]);
  const [attTrend, setAttTrend] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [students, classes] = await Promise.all([
          academyService.getStudents(academy.id),
          academyService.getClasses(academy.id),
        ]);

        // 요일별 수업 분포 (월~일) — 추가 조회 없음
        const dist = WEEK_ORDER.map((d) => {
          const cs = classes.filter((c) => (c.schedule || []).some((s) => s.day === d));
          return { day: d, classes: cs.length, students: cs.reduce((n, c) => n + (c.studentIds || []).length, 0) };
        });

        // 최근 14일 출석률 추이 (요일에 수업 있는 날만 조회)
        const days = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          days.push(d);
        }
        const trend = await Promise.all(
          days.map(async (d) => {
            const wd = KO_DAYS[d.getDay()];
            const dayClasses = classes.filter((c) => (c.schedule || []).some((s) => s.day === wd));
            let present = 0;
            let total = 0;
            await Promise.all(
              dayClasses.map(async (c) => {
                const att = await academyService.getClassAttendance(academy.id, c.id, fmtDate(d));
                const recs = att.records || {};
                (c.studentIds || []).forEach((sid) => {
                  total += 1;
                  const r = recs[sid];
                  const status = typeof r === 'string' ? r : r?.status;
                  if (status === 'present' || status === 'late') present += 1;
                });
              })
            );
            return { label: `${d.getMonth() + 1}/${d.getDate()}`, rate: total ? Math.round((present / total) * 100) : 0, total, present };
          })
        );

        // 오늘 수업 목록 + 오늘 출석 통계 (추이 마지막 = 오늘)
        const wdToday = KO_DAYS[new Date().getDay()];
        const todays = classes
          .filter((c) => (c.schedule || []).some((s) => s.day === wdToday))
          .map((c) => ({ ...c, time: (c.schedule.find((s) => s.day === wdToday) || {}).time || '' }))
          .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
        const today = trend[trend.length - 1] || { present: 0, total: 0 };

        if (!alive) return;
        setStats({ students: students.length, classes: classes.length, present: today.present, attTotal: today.total });
        setTodayClasses(todays);
        setWeekDist(dist);
        setAttTrend(trend);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [academy.id]);

  const attRate = stats.attTotal ? Math.round((stats.present / stats.attTotal) * 100) : 0;
  const todayWd = KO_DAYS[new Date().getDay()];
  const maxClasses = Math.max(0, ...weekDist.map((w) => w.classes));
  const validTrend = attTrend.filter((t) => t.total > 0);
  const avgRate = validTrend.length ? Math.round(validTrend.reduce((n, t) => n + t.rate, 0) / validTrend.length) : 0;
  const hasAtt = validTrend.length > 0;

  return (
    <>
      <Head>
        <h2>운영 대시보드</h2>
        <p>{new Date().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })} 기준</p>
      </Head>

      <StatGrid>
        <StatCard>
          <StatIcon $c="#4A7CFE"><HiOutlineUsers size={24} /></StatIcon>
          <StatBody><div className="v">{loading ? '–' : stats.students}</div><div className="l">전체 학생</div></StatBody>
        </StatCard>
        <StatCard>
          <StatIcon $c="#22C55E"><HiOutlineBookOpen size={24} /></StatIcon>
          <StatBody><div className="v">{loading ? '–' : stats.classes}</div><div className="l">개설 수업</div></StatBody>
        </StatCard>
        <StatCard>
          <StatIcon $c="#6366F1"><IoCalendarOutline size={24} /></StatIcon>
          <StatBody><div className="v">{loading ? '–' : todayClasses.length}</div><div className="l">오늘 수업</div></StatBody>
        </StatCard>
        <StatCard>
          <StatIcon $c="#F59E0B"><IoCheckmarkCircle size={24} /></StatIcon>
          <StatBody>
            <div className="v">{loading ? '–' : `${attRate}%`}</div>
            <div className="l">오늘 출석률 {stats.attTotal ? `(${stats.present}/${stats.attTotal})` : ''}</div>
          </StatBody>
        </StatCard>
      </StatGrid>

      <Card>
        <CardTitle>오늘 수업 ({KO_DAYS[new Date().getDay()]}요일)</CardTitle>
        {loading ? (
          <Empty>불러오는 중…</Empty>
        ) : todayClasses.length === 0 ? (
          <Empty>오늘 예정된 수업이 없어요.</Empty>
        ) : (
          <List>
            {todayClasses.map((c) => (
              <Row key={c.id} onClick={() => navigate('/console/classes')}>
                <Dot $c={c.color || '#4F46E5'} />
                <Time>{c.time || '시간 미정'}</Time>
                <Name>{c.name}</Name>
                <Count>{(c.studentIds || []).length}명</Count>
              </Row>
            ))}
          </List>
        )}
      </Card>

      <ChartGrid>
        {/* 최근 2주 출석률 추이 */}
        <Card>
          <ChartHead>
            <ChartTitle>출석률 추이</ChartTitle>
            <ChartMeta>최근 2주 평균 {hasAtt ? `${avgRate}%` : '–'}</ChartMeta>
          </ChartHead>
          {loading ? (
            <Empty>불러오는 중…</Empty>
          ) : !hasAtt ? (
            <Empty>출석 기록이 아직 없어요.</Empty>
          ) : (
            <>
              <Trend>
                {attTrend.map((t, i) => (
                  <TrendCol key={t.label} title={`${t.label} · ${t.total ? `${t.rate}%` : '기록 없음'}`}>
                    <TrendBar $h={t.rate} $empty={!t.total} $today={i === attTrend.length - 1} />
                  </TrendCol>
                ))}
              </Trend>
              <TrendAxis>
                <span>{attTrend[0]?.label}</span>
                <span>오늘</span>
              </TrendAxis>
            </>
          )}
        </Card>

        {/* 요일별 수업 분포 */}
        <Card>
          <ChartHead>
            <ChartTitle>요일별 수업 분포</ChartTitle>
            <ChartMeta>주 {weekDist.reduce((n, w) => n + w.classes, 0)}회</ChartMeta>
          </ChartHead>
          {loading ? (
            <Empty>불러오는 중…</Empty>
          ) : maxClasses === 0 ? (
            <Empty>등록된 수업이 없어요.</Empty>
          ) : (
            <Week>
              {weekDist.map((w) => (
                <WeekCol key={w.day}>
                  <WeekBarWrap>
                    <WeekCount>{w.classes || ''}</WeekCount>
                    <WeekBar $h={maxClasses ? Math.round((w.classes / maxClasses) * 100) : 0} $today={w.day === todayWd} />
                  </WeekBarWrap>
                  <WeekDay $today={w.day === todayWd}>{w.day}</WeekDay>
                </WeekCol>
              ))}
            </Week>
          )}
        </Card>
      </ChartGrid>
    </>
  );
};

export default ConsoleDashboard;

const Head = styled.div`
  margin-bottom: 22px;
  h2 { font-size: 22px; font-weight: 800; color: #111827; }
  p { font-size: 13px; color: #9CA3AF; margin-top: 5px; }
`;

const CardTitle = styled.h3` font-size: 15px; font-weight: 800; color: #1F2937; margin-bottom: 14px; `;

const List = styled.div` display: flex; flex-direction: column; `;

const Row = styled.button`
  display: flex; align-items: center; gap: 14px; width: 100%;
  padding: 13px 8px; border-bottom: 1px solid #F1F3F7; text-align: left;
  &:last-child { border-bottom: none; }
  &:hover { background: #FBFCFE; }
`;

const Dot = styled.span` width: 10px; height: 10px; border-radius: 50%; background: ${({ $c }) => $c}; flex-shrink: 0; `;
const Time = styled.span` font-size: 14px; font-weight: 700; color: #4A7CFE; width: 64px; `;
const Name = styled.span` flex: 1; font-size: 15px; font-weight: 600; color: #1F2937; `;
const Count = styled.span` font-size: 13px; color: #9CA3AF; `;

/* ── 차트 ── */
const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ChartHead = styled.div`
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 16px;
`;
const ChartTitle = styled.h3` font-size: 15px; font-weight: 800; color: #1F2937; `;
const ChartMeta = styled.span` font-size: 12px; font-weight: 700; color: #4A7CFE; `;

/* 출석률 추이 (14일) */
const Trend = styled.div`
  display: flex; align-items: flex-end; gap: 4px; height: 150px;
`;
const TrendCol = styled.div`
  flex: 1; height: 100%; display: flex; align-items: flex-end;
`;
const TrendBar = styled.div`
  width: 100%;
  height: ${({ $h }) => $h}%;
  min-height: ${({ $empty, $h }) => ($empty ? '3px' : $h > 0 ? '4px' : '0')};
  border-radius: 4px 4px 0 0;
  background: ${({ $today, $empty }) => ($empty ? '#EEF1F6' : $today ? '#4A7CFE' : '#BFD3FA')};
  transition: height 0.4s ease;
`;
const TrendAxis = styled.div`
  display: flex; justify-content: space-between; margin-top: 10px;
  font-size: 11px; color: #B6BCC8;
`;

/* 요일별 수업 분포 */
const Week = styled.div`
  display: flex; align-items: flex-end; gap: 10px; height: 150px;
`;
const WeekCol = styled.div`
  flex: 1; height: 100%; display: flex; flex-direction: column; align-items: center;
`;
const WeekBarWrap = styled.div`
  flex: 1; width: 100%; min-height: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-end;
`;
const WeekCount = styled.span`
  font-size: 12px; font-weight: 700; color: #6B7280; margin-bottom: 6px; min-height: 14px;
`;
const WeekBar = styled.div`
  width: 64%; max-width: 34px;
  height: ${({ $h }) => $h}%;
  min-height: ${({ $h }) => ($h > 0 ? '6px' : '0')};
  border-radius: 6px 6px 0 0;
  background: ${({ $today }) => ($today ? '#4A7CFE' : '#DCE6FB')};
  transition: height 0.4s ease;
`;
const WeekDay = styled.span`
  margin-top: 8px; font-size: 12px;
  font-weight: ${({ $today }) => ($today ? 800 : 600)};
  color: ${({ $today }) => ($today ? '#4A7CFE' : '#9CA3AF')};
`;
